// device.ts - ไฟล์สำหรับกำหนด Type/Interface ที่เกี่ยวข้องกับอุปกรณ์

// นำเข้า interface จาก productService
import { Product } from '../services/productService';
import { RentalHoldInfo } from './rentalHold';

// User info for tracking
export interface UserInfo {
  id: number;
  username: string;
  full_name?: string;
}

// DeviceType interfaces
export interface DeviceType {
  id: number;
  code: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: UserInfo;
  created_by_id?: number;
  updated_by?: UserInfo;
  updated_by_id?: number;
}

export interface CreateDeviceTypeRequest {
  code: string;
  name: string;
  description?: string;
  is_active?: boolean;
}

export interface UpdateDeviceTypeRequest {
  code?: string;
  name?: string;
  description?: string;
  is_active?: boolean;
}

// DeviceModel interfaces (รุ่นอุปกรณ์)
export interface DeviceModel {
  id: number;
  code: string;
  name: string;
  brand_id: number;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  created_by?: UserInfo;
  created_by_id?: number;
  updated_by?: UserInfo;
  updated_by_id?: number;
  brand?: {
    id: number;
    code: string;
    name: string;
  };
}

export interface CreateDeviceModelRequest {
  code: string;
  name: string;
  brand_id: number;
  description?: string;
  is_active?: boolean;
}

export interface UpdateDeviceModelRequest {
  code?: string;
  name?: string;
  brand_id?: number;
  description?: string;
  is_active?: boolean;
}

// สถานะอุปกรณ์ - ตรงกับ Backend enum values
export enum DeviceStatus {
  AVAILABLE = "AVAILABLE", // พร้อมใช้งาน
  RENTED = "RENTED",       // กำลังใช้งาน (เช่าอยู่)
  // Phase 2 คลังอุปกรณ์ (PO 2026-09-07): เบิก/ยืมไปใช้ภายใน (ไม่ผูกสัญญาลูกค้า)
  // **ระบบตั้งค่านี้เองตอนสร้างใบเบิก** และคืนกลับ AVAILABLE ตอนรับคืนสภาพดี —
  // ฟอร์มแก้ไขอุปกรณ์จึงไม่มีตัวเลือกนี้ให้เลือกเอง และล็อกช่องสถานะเมื่อเครื่องอยู่สถานะนี้
  // (แก้ด้วยมือ = ยอด/ใบเบิกไม่ตรงกัน โดยไม่มี ledger บันทึก)
  ISSUED = "ISSUED",       // เบิกไปใช้ภายใน
  // ใบงานเตรียมเครื่อง (PO 2026-09-08): ของถูกกันไว้ให้ดีลที่ปิดแล้วแต่ยังไม่ทำสัญญา
  // **ยังอยู่ในคลัง ยังไม่ส่งมอบ** — ต่างจาก RENTED (ลูกค้าถืออยู่) และ ISSUED (เบิกใช้ภายใน)
  // ระบบตั้งค่านี้เองตอน Support จับคู่เครื่องเข้าใบงาน ฟอร์มแก้ไขอุปกรณ์จึงไม่มีให้เลือกเอง
  RESERVED = "RESERVED",   // จองไว้ (ใบงานเตรียมเครื่อง)
  MAINTENANCE = "MAINTENANCE", // ซ่อมบำรุง
  DAMAGED = "DAMAGED",     // ชำรุด
  LOST = "LOST",           // สูญหาย
  DISPOSED = "DISPOSED"    // จำหน่ายออก
}

// สภาพอุปกรณ์ - ตรงกับ Backend enum values
export enum DeviceCondition {
  NEW = "NEW",              // ใหม่
  USED = "USED",            // มือสอง
  REFURBISHED = "REFURBISHED" // ซ่อมแล้ว
}

// ข้อมูลผู้จำหน่ายแบบย่อ
export interface SupplierInfo {
  id: number;
  code: string;
  name: string;
}


// ประเภทของซิมการ์ด
export enum SimType {
  PREPAID = "prepaid",       // เติมเงิน
  POSTPAID = "postpaid",     // รายเดือน
  YEARLY = "yearly",         // รายปี
  CORPORATE = "corporate",   // องค์กร
  OTHER = "other"            // อื่นๆ
}

// ข้อมูลซิมการ์ด
export interface DeviceSim {
  sim_type: SimType;        // ประเภทซิม
  provider: string;         // ค่ายมือถือ (เช่น True, AIS, Dtac)
  operator?: string;        // alias for provider (legacy)
  price: number;            // ราคาซิม
  phone_number: string;     // เบอร์โทรศัพท์
  activation_code?: string; // รหัส Activate
  expiry_date?: string;     // วันหมดอายุซิม
  registered_by?: string;   // ชื่อผู้ลงทะเบียน
  registration_name?: string; // ชื่อทะเบียนผู้ลงทะเบียน
}

// ข้อมูลสัญญาเช่า/ซื้อ
export interface DeviceContract {
  company_name: string;     // บริษัทฯหรือผู้ที่ทำสัญญา
  site_name?: string;       // ชื่อโครงการ/ไซต์
  site_name_full?: string;  // ชื่อโครงการ/ไซต์แบบเต็ม
  start_date?: string;      // วันเริ่มสัญญา
  end_date?: string;        // วันที่หมดสัญญา
  rental_price?: number;    // ราคาเช่าต่อเดือน
  deposit_amount?: number;  // ค่ามัดจำ
  deposit_date?: string;    // วันที่โอนค่ามัดจำ
  deposit_status?: string;  // สถานะค่ามัดจำ (เช่น "คืนแล้ว")
  sales_person?: string;    // พนักงานขาย
  delivery_date?: string;   // วันที่ส่งมอบ
}

// ข้อมูลการบำรุงรักษา - สำหรับข้อมูล Clear Cache
export interface DeviceClearCache {
  plan_frequency?: number;  // ความถี่ในการล้างแคช (จำนวนเดือน)
  last_clear_date?: string; // วันที่ล้างแคชล่าสุด
  cleared_by?: string;      // ผู้ที่ล้างแคชล่าสุด
}

// ข้อมูลการติดต่อและอื่นๆ
export interface DeviceContact {
  contact_name?: string;    // ชื่อผู้ติดต่อ
  contact_phone?: string;   // เบอร์ติดต่อ
  site_phone?: string;      // เบอร์โทรโครงการ/นิติฯ
  telegram?: string;        // Telegram ID/username
}

// อินเตอร์เฟซหลักสำหรับข้อมูลอุปกรณ์
export interface Device {
  // ข้อมูลพื้นฐาน
  id: number;
  code: string;                 // รหัสเครื่อง เช่น 201-V001
  customer_code?: string;       // รหัสลูกค้า
  // feedback amintra 2026-09-08: ของที่ใบตามเก็บยังเปิดค้าง = อาจยังอยู่กับลูกค้า
  // แม้ devices.status จะเป็น AVAILABLE (prod มี 42 เครื่องสภาพนี้ · 26 ตัวเป็น AVAILABLE)
  has_pending_return?: boolean;
  pending_return_count?: number;
  // Fix ค (20 ก.ย. 2026) — "พร้อมใช้งานแต่ยังถูกล็อก" ด้วยสัญญาที่ยังไม่จบ (DRAFT/ACTIVE/
  // PENDING_CANCELLATION) — null เมื่อไม่ล็อก · ดู types/rentalHold.ts + API_CONTRACT.md ค-1
  rental_hold?: RentalHoldInfo | null;
  device_type_id?: number;      // รหัสประเภทอุปกรณ์ (เชื่อมโยงกับตาราง device_types)
  device_type?: DeviceType;     // ข้อมูลประเภทอุปกรณ์ (โทรศัพท์, แท็บเล็ต, ฯลฯ)
  brand_id?: number;            // รหัสยี่ห้อ (เชื่อมโยงกับตาราง brands)
  brand?: { id: number; name: string; code?: string }; // ข้อมูลยี่ห้อเต็ม (เมื่อ BE include relation)
  device_model_id?: number;     // รหัสรุ่นอุปกรณ์ (เชื่อมโยงกับตาราง device_models)
  device_model?: DeviceModel;   // ข้อมูลรุ่นอุปกรณ์
  model: string;                // รุ่น (deprecated - ใช้ device_model แทน)
  serial_number?: string;       // หมายเลขซีเรียล (optional for non-serialized)
  imei?: string;                // IMEI
  
  // ข้อมูลสถานะและการใช้งาน
  status: DeviceStatus;         // สถานะอุปกรณ์
  is_k_connect_enabled: boolean;// ใช้งาน K-Connect หรือไม่
  activation_code?: string;     // รหัส Activate
  purchase_date?: string;       // วันที่ซื้อ
  warranty_expire_date?: string;// วันหมดอายุประกัน
  last_maintenance_date?: string;// วันที่บำรุงรักษาล่าสุด
  // AnyDesk ปัจจุบัน = ของรายการเช่าในสัญญาที่เครื่องอยู่ (23 ก.ย. 2026 · ค่าเดียวทุกหน้าจอ)
  // null = เครื่องไม่ได้อยู่ในสัญญา · แก้ผ่าน remoteAccessService.updateDevice (ไม่ใช่ฟอร์มอุปกรณ์)
  current_anydesk_id?: string | null;
  current_anydesk_rental_number?: string | null;

  // ข้อมูล Inventory Management
  is_serialized: boolean;       // true = ติดตาม serial, false = นับจำนวน
  quantity: number;             // จำนวนคงคลัง (สำหรับ non-serialized)
  unit: string;                 // หน่วยนับ (เครื่อง, ชิ้น, เส้น, อัน)
  minimum_quantity: number;     // จำนวนขั้นต่ำ

  // ข้อมูลการจัดซื้อ/ครุภัณฑ์
  supplier_id?: number;         // รหัสผู้จำหน่าย (FK)
  supplier?: SupplierInfo;      // ข้อมูลผู้จำหน่าย
  purchase_price?: number;      // ราคาซื้อ/ต้นทุน
  invoice_number?: string;      // เลขที่ใบกำกับภาษี
  receive_date?: string;        // วันที่รับเข้าคลัง
  condition?: DeviceCondition;  // สภาพสินค้า
  book_value?: number;          // มูลค่าทางบัญชี
  asset_number?: string;        // เลขครุภัณฑ์

  // ข้อมูลเพิ่มเติม
  accessories?: string;         // อุปกรณ์เสริม (สายคล้อง, แท่นชาร์จ, ฯลฯ)
  notes?: string;               // หมายเหตุทั่วไป
  additional_notes?: string;    // Note เพิ่มเติม
  location?: string;            // สถานที่ตั้ง

  // New fields for data migration
  location_note?: string;       // หมายเหตุตำแหน่ง เช่น ป้อมหน้า, ป้อมสกัด
  device_number?: number;       // ลำดับเครื่องในโครงการ (เครื่อง 1, 2)

  // Phase 2 คลังอุปกรณ์ (PO 2026-09-07) — มีค่าเมื่อ status = ISSUED เท่านั้น
  // ตารางอุปกรณ์แสดงชื่อผู้ยืมใต้ป้ายสถานะ เพื่อให้รู้ว่า "เครื่องอยู่กับใคร" โดยไม่ต้องเปิดใบเบิก
  // optional เพราะ backend รุ่นก่อนหน้าไม่ส่งฟิลด์นี้ (deploy skew)
  issued_to_display?: string | null;
  current_stock_issue_id?: number | null;
  current_stock_issue_number?: string | null;

  // ข้อมูลเพิ่มเติมที่เชื่อมโยงกับอินเตอร์เฟซอื่น
  sim?: DeviceSim;              // ข้อมูลซิมการ์ด
  contract?: DeviceContract;    // ข้อมูลสัญญาเช่า
  clear_cache?: DeviceClearCache; // ข้อมูล Clear Cache
  contact?: DeviceContact;      // ข้อมูลการติดต่อ
  
  // ข้อมูลระบบ
  created_at: string;
  updated_at?: string;
  created_by?: UserInfo;        // ข้อมูลผู้สร้าง
  created_by_id?: number;       // ID ผู้สร้าง
  updated_by?: UserInfo;        // ข้อมูลผู้แก้ไขล่าสุด
  updated_by_id?: number;       // ID ผู้แก้ไขล่าสุด
}

// อินเตอร์เฟซสำหรับฟอร์มข้อมูลอุปกรณ์
export interface DeviceFormData {
  // ข้อมูลพื้นฐาน
  code: string;
  customer_code?: string;
  device_type_id?: number;
  brand_id?: number;
  device_model_id?: number;
  model: string;
  serial_number?: string;  // optional for non-serialized
  imei?: string;
  
  // ข้อมูลสถานะและการใช้งาน
  status: DeviceStatus;
  is_k_connect_enabled: boolean;
  activation_code?: string;
  purchase_date?: string;
  warranty_expire_date?: string;
  last_maintenance_date?: string;
  // ไม่มี anydesk_* ในฟอร์มอุปกรณ์ — AnyDesk อยู่ที่รายการเช่า (remoteAccessService)

  // ข้อมูล Inventory Management
  is_serialized: boolean;
  quantity: number;
  unit: string;
  minimum_quantity: number;

  // ข้อมูลการจัดซื้อ/ครุภัณฑ์
  supplier_id?: number;           // รหัสผู้จำหน่าย
  purchase_price?: number;        // ราคาซื้อ/ต้นทุน
  invoice_number?: string;        // เลขที่ใบกำกับภาษี
  receive_date?: string;          // วันที่รับเข้าคลัง
  condition?: DeviceCondition;    // สภาพสินค้า
  book_value?: number;            // มูลค่าทางบัญชี
  asset_number?: string;          // เลขครุภัณฑ์

  // ข้อมูลเพิ่มเติม
  accessories?: string;
  notes?: string;
  additional_notes?: string;
  location?: string;

  // New fields for data migration
  location_note?: string;
  device_number?: number;

  // ข้อมูลซิมการ์ด
  sim_type?: SimType;
  sim_provider?: string;
  sim_price?: number | string;
  sim_phone_number?: string;
  sim_activation_code?: string;
  sim_expiry_date?: string;
  sim_registered_by?: string;
  
  // ข้อมูลสัญญาเช่า
  contract_company_name?: string;
  contract_site_name?: string;
  contract_site_name_full?: string;
  contract_start_date?: string;
  contract_end_date?: string;
  contract_rental_price?: number | string;
  contract_deposit_amount?: number | string;
  contract_deposit_date?: string;
  contract_deposit_status?: string;
  contract_sales_person?: string;
  contract_delivery_date?: string;
  
  // ข้อมูล Clear Cache
  clear_cache_plan_frequency?: number | string;
  clear_cache_last_date?: string;
  clear_cache_by?: string;
  
  // ข้อมูลการติดต่อ
  contact_name?: string;
  contact_phone?: string;
  site_phone?: string;
  telegram?: string;
}